/**
 * Preview dev-only del dashboard de Tesorería (Sprint 17 Fase 2).
 *
 * Renderiza `TesoreriaHomeView` (componente puro) con mock data + switch
 * entre escenarios. Incluye charts (Recharts) + sparklines + feed
 * de actividad. Vista año calendario (C2).
 *
 * Acceso: solo en builds de desarrollo (convención #15 corolario).
 */
import { useState } from 'react';
import {
  TesoreriaHomeView,
  type TesoreriaHomeViewProps,
} from '../tesoreria/TesoreriaHome';
import type { PagoRead } from '../../lib/api/types';

type Escenario = 'completo' | 'vacio' | 'cargando' | 'error';

function hoy(): string {
  return new Date().toISOString().slice(0, 10);
}

function mesActualISO(): string {
  return hoy().slice(0, 7) + '-01';
}

function anioActual(): number {
  return new Date().getFullYear();
}

function mesNumActual(): number {
  return new Date().getMonth() + 1;
}

function mockPago(
  id: string,
  fecha: string,
  monto: number,
  remitente: string,
): PagoRead {
  return {
    id,
    lote_id: null,
    lote_identificador: null,
    persona_id: null,
    monto_total: monto,
    fecha_transferencia: fecha,
    num_operacion: `OP-${id}`,
    remitente_nombre: remitente,
    remitente_email: null,
    mensaje_email: '',
    email_template_detectado: null,
    comprobante_url: null,
    estado: 'por_distribuir',
    compensacion_id: null,
    aprobado_por_usuario_id: null,
    aprobado_at: null,
    es_test: false,
    creado_at: '2026-05-20T00:00:00Z',
    actualizado_at: '2026-05-20T00:00:00Z',
  } as unknown as PagoRead;
}

function buildSparklineData(
  base: number,
  variacion: number,
  meses: number,
): { mes: string; monto: number }[] {
  const y = anioActual();
  return Array.from({ length: meses }, (_, i) => {
    const m = i + 1;
    const noise = Math.sin(i * 1.2) * variacion;
    return {
      mes: `${y}-${String(m).padStart(2, '0')}-01`,
      monto: Math.max(0, Math.round(base + noise + i * variacion * 0.15)),
    };
  });
}

const ESCENARIOS: Record<
  Escenario,
  Omit<TesoreriaHomeViewProps, 'donutScope' | 'onDonutScopeChange'>
> = {
  completo: {
    loading: false,
    error: null,
    resumen: {
      anio_activo: new Date().getFullYear(),
      mes_actual: mesActualISO(),
      recaudacion_ytd: 9_300_000,
      gastos_ytd: 4_750_000,
      saldo_neto_ytd: 4_550_000,
      saldo_bancario: 12_500_000,
      saldo_bancario_disponible: true,
      total_depositos: 2500000,
      tendencia_recaudacion_pct: 8.5,
      tendencia_gastos_pct: -3.2,
      tendencia_saldo_pct: 1.4,
      tendencia_casas_contra_pct: 33.3,
      pagos_sin_clasificar: { count: 3, monto_total: 145_000 },
      casas_saldo_contra: { count: 4, monto_total: 220_000 },
      casas_atrasadas_top5: [
        { lote_id: 'l1', identificador: '6951', monto_adeudado: 90_000, titular: 'Manuel Acuña', ultimo_pago_fecha: '2026-03-15', estado: 'suspendida' },
        { lote_id: 'l2', identificador: '6846', monto_adeudado: 60_000, titular: 'Patricia Soto', ultimo_pago_fecha: '2026-04-02', estado: 'atrasada' },
        { lote_id: 'l3', identificador: '6834', monto_adeudado: 40_000, titular: 'Rosa Vásquez', ultimo_pago_fecha: '2026-04-20', estado: 'atrasada' },
        { lote_id: 'l4', identificador: '6913', monto_adeudado: 30_000, titular: 'Luis Muñoz', ultimo_pago_fecha: null, estado: 'al_dia_pendiente_semestre' },
      ],
    },
    sparklines: {
      anio: anioActual(),
      saldo_bancario: buildSparklineData(11_500_000, 800_000, mesNumActual()),
      recaudacion: buildSparklineData(1_400_000, 200_000, mesNumActual()),
      gastos: buildSparklineData(750_000, 150_000, mesNumActual()),
    },
    comparativa: {
      anio: anioActual(),
      meses: Array.from({ length: 12 }, (_, i) => {
        const m = i + 1;
        const esFuturo = m > mesNumActual();
        const esParcial = m === mesNumActual();
        return {
          mes: `${anioActual()}-${String(m).padStart(2, '0')}-01`,
          recaudacion: esFuturo
            ? 0
            : Math.round(1_400_000 + Math.sin(i) * 200_000),
          gastos: esFuturo ? 0 : Math.round(750_000 + Math.cos(i) * 150_000),
          es_futuro: esFuturo,
          es_parcial: esParcial,
        };
      }),
    },
    gastosCategoria: {
      mes: mesActualISO(),
      total: 850_000,
      items: [
        {
          categoria_id: 'c1',
          categoria_nombre: 'Mantención',
          categoria_color: '#5fd9b1',
          monto: 320_000,
          hijos: [
            {
              categoria_id: 'c1a',
              categoria_nombre: 'Áreas verdes',
              categoria_color: '#5fd9b1',
              monto: 180_000,
            },
            {
              categoria_id: 'c1b',
              categoria_nombre: 'Plomería',
              categoria_color: '#5fd9b1',
              monto: 100_000,
            },
            {
              categoria_id: 'c1c',
              categoria_nombre: 'Electricidad',
              categoria_color: '#5fd9b1',
              monto: 40_000,
            },
          ],
        },
        {
          categoria_id: 'c2',
          categoria_nombre: 'Servicios',
          categoria_color: '#7ab8ff',
          monto: 250_000,
          hijos: [
            {
              categoria_id: 'c2a',
              categoria_nombre: 'Agua',
              categoria_color: '#7ab8ff',
              monto: 130_000,
            },
            {
              categoria_id: 'c2b',
              categoria_nombre: 'Electricidad común',
              categoria_color: '#7ab8ff',
              monto: 120_000,
            },
          ],
        },
        {
          categoria_id: 'c3',
          categoria_nombre: 'Seguridad',
          categoria_color: '#ffb775',
          monto: 180_000,
          hijos: [],
        },
        {
          categoria_id: 'c4',
          categoria_nombre: 'Administración',
          categoria_color: '#b89bff',
          monto: 100_000,
          hijos: [],
        },
      ],
    },
    actividad: {
      eventos: [
        {
          tipo: 'pago_aprobado',
          timestamp: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
          descripcion: 'Pago aprobado · Casa 6951',
          monto: 48_500,
          lote_identificador: '6951',
          entidad_id: 'p1',
          enlace: '/tesoreria/pagos',
        },
        {
          tipo: 'gasto_registrado',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
          descripcion: 'Gasto · Reparación bomba de agua',
          monto: 145_000,
          lote_identificador: null,
          entidad_id: 'g1',
          enlace: '/tesoreria/gastos',
        },
        {
          tipo: 'compensacion_aplicada',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
          descripcion: 'Compensación creada · Casa 6913',
          monto: 80_000,
          lote_identificador: '6913',
          entidad_id: 'c1',
          enlace: '/tesoreria/compensaciones',
        },
        {
          tipo: 'cierre_firmado',
          timestamp: new Date(
            Date.now() - 1000 * 60 * 60 * 24 * 5,
          ).toISOString(),
          descripcion: 'Cierre semestral generado · 2025-S2',
          monto: null,
          lote_identificador: null,
          entidad_id: 'cs1',
          enlace: '/tesoreria/reportes',
        },
      ],
    },
    colaPagos: [
      mockPago('p1', '2026-05-20', 48_500, 'Juan Pérez'),
      mockPago('p2', '2026-05-19', 48_500, 'Maria Soto'),
      mockPago('p3', '2026-05-18', 30_000, 'Pedro Gomez'),
    ],
    solicitudesEsperandoCobro: { count: 3, masDe30Dias: 1 },
  },
  vacio: {
    loading: false,
    error: null,
    resumen: {
      anio_activo: new Date().getFullYear(),
      mes_actual: mesActualISO(),
      recaudacion_ytd: 0,
      gastos_ytd: 0,
      saldo_neto_ytd: 0,
      saldo_bancario: 0,
      saldo_bancario_disponible: false,
      total_depositos: 0,
      tendencia_recaudacion_pct: null,
      tendencia_gastos_pct: null,
      tendencia_saldo_pct: null,
      tendencia_casas_contra_pct: null,
      pagos_sin_clasificar: { count: 0, monto_total: 0 },
      casas_saldo_contra: { count: 0, monto_total: 0 },
      casas_atrasadas_top5: [],
    },
    sparklines: {
      anio: anioActual(),
      saldo_bancario: [],
      recaudacion: [],
      gastos: [],
    },
    comparativa: {
      anio: anioActual(),
      meses: Array.from({ length: 12 }, (_, i) => {
        const m = i + 1;
        return {
          mes: `${anioActual()}-${String(m).padStart(2, '0')}-01`,
          recaudacion: 0,
          gastos: 0,
          es_futuro: m > mesNumActual(),
          es_parcial: m === mesNumActual(),
        };
      }),
    },
    gastosCategoria: {
      mes: mesActualISO(),
      total: 0,
      items: [],
    },
    actividad: { eventos: [] },
    colaPagos: [],
    solicitudesEsperandoCobro: { count: 0, masDe30Dias: 0 },
  },
  cargando: {
    loading: true,
    error: null,
    resumen: null,
    sparklines: null,
    comparativa: null,
    gastosCategoria: null,
    actividad: null,
    colaPagos: [],
    solicitudesEsperandoCobro: { count: 0, masDe30Dias: 0 },
  },
  error: {
    loading: false,
    error: 'No se pudo conectar al backend (mock error para preview)',
    resumen: null,
    sparklines: null,
    comparativa: null,
    gastosCategoria: null,
    actividad: null,
    colaPagos: [],
    solicitudesEsperandoCobro: { count: 0, masDe30Dias: 0 },
  },
};

const SIDEBAR_ITEMS = [
  { label: 'Inicio', active: true },
  { label: 'Casas', proximamente: true },
  { label: 'Pagos' },
  { label: 'Ingresos varios', proximamente: true },
  { label: 'Gastos', proximamente: true },
  { label: 'Compensaciones' },
  { label: 'Recursos' },
  { label: 'Cuotas', proximamente: true },
  { label: 'Suspensiones', proximamente: true },
  { label: 'Cierres', proximamente: true },
  { label: 'Reportes', proximamente: true },
];

export function TesoreriaHomePreview() {
  const [escenario, setEscenario] = useState<Escenario>('completo');
  const [donutScope, setDonutScope] = useState<'anio' | 'mes'>('anio');
  const props = ESCENARIOS[escenario];

  return (
    <div className="min-h-screen bg-bg-page text-text-primary flex">
      <aside className="hidden md:flex w-64 bg-bg-sunken border-r-[0.5px] border-white/[0.08] flex-col">
        <div className="px-6 py-5 border-b-[0.5px] border-white/[0.08]">
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <div className="text-base text-text-primary font-medium mt-1">
            Barrio Calbucura
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {SIDEBAR_ITEMS.map((it) => (
            <div
              key={it.label}
              className={`flex items-center justify-between px-3 py-2 rounded text-sm ${
                it.active
                  ? 'bg-bg-card text-text-primary font-medium'
                  : 'text-text-secondary'
              } ${it.proximamente ? 'opacity-70' : ''}`}
            >
              <span>{it.label}</span>
              {it.proximamente && (
                <span className="text-[10px] uppercase tracking-wide text-text-muted">
                  pronto
                </span>
              )}
            </div>
          ))}
        </nav>
        <div className="px-6 py-4 border-t-[0.5px] border-white/[0.08] text-xs text-text-muted">
          Portal interno. Las acciones aquí afectan los datos del barrio.
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-bg-card border-b-[0.5px] border-white/[0.08] px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="text-sm text-text-secondary">
            Panel del tesorero
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-text-muted hidden sm:inline">
              Escenario:
            </span>
            {(['completo', 'vacio', 'cargando', 'error'] as const).map(
              (e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEscenario(e)}
                  className={`px-2 py-1 rounded border ${
                    escenario === e
                      ? 'border-orange text-orange'
                      : 'border-white/[0.12] text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {e}
                </button>
              ),
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <TesoreriaHomeView
            {...props}
            donutScope={donutScope}
            onDonutScopeChange={setDonutScope}
          />
        </main>

        <footer className="bg-bg-page px-4 md:px-8 py-3 text-[11px] text-text-muted border-t border-white/[0.06]">
          Preview dev-only — sidebar y header reproducidos. Sesión real
          requiere login en localhost:5173/login.
        </footer>
      </div>
    </div>
  );
}
